import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Clock,
  Target,
  FileText,
  Calendar,
  Crown,
  ArrowRight,
  Play,
  ChevronDown,
  ChevronUp,
  Edit3,
  Loader2,
  Sparkles,
  Search,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  ExternalLink,
  X,
  Gift
} from 'lucide-react';
import { Button, Card, Badge, Alert, LoadingSpinner, TaskProgressPanel, TruncatedText } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { usePreferences } from '@/hooks/usePreferences';
import { useActiveTask } from '@/hooks/useActiveTask';
import { useProgressStream } from '@/hooks/useProgressStream';
import { api } from '@/lib/api';
import type { CanGenerateResponse, Preferences, RecentIssue, GenerationProgress, TaskProgress, DeliverySchedule } from '@/types';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
}

function StatCard({ icon, label, value, subtext }: StatCardProps) {
  return (
    <Card>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-text-secondary text-xs sm:text-sm">{label}</p>
          <p className="text-lg sm:text-xl font-semibold truncate">{value}</p>
          {subtext && <p className="text-text-muted text-xs sm:text-sm truncate">{subtext}</p>}
        </div>
      </div>
    </Card>
  );
}

interface TopicCardProps {
  topicName: string;
  promptTemplate?: string;
  customPrompt?: string;
  presetId?: string;
}

function TopicCard({ topicName, promptTemplate, customPrompt, presetId }: TopicCardProps) {
  const navigate = useNavigate();

  // Determine what to show: custom prompt or preset template
  const displayPrompt = customPrompt || promptTemplate || '';
  const isCustom = !presetId || !!customPrompt;
  const hasPrompt = displayPrompt.length > 0;

  return (
    <Card>
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <Target className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-text-secondary text-xs sm:text-sm">Topic</p>
            {hasPrompt && (
              <Badge variant={isCustom ? 'primary' : 'info'} className="text-xs">
                {isCustom ? 'Custom' : 'Preset'}
              </Badge>
            )}
          </div>
          <TruncatedText text={topicName || 'Not set'} className="text-lg sm:text-xl font-semibold" />

          {/* Prompt display */}
          {hasPrompt ? (
            <TruncatedText text={displayPrompt} lines={2} className="text-text-muted text-xs sm:text-sm mt-2 leading-relaxed" />
          ) : (
            <p className="text-text-muted text-xs sm:text-sm mt-2 italic">
              No prompt configured
            </p>
          )}

          {/* Link to settings */}
          <button
            onClick={() => navigate('/preferences')}
            className="text-xs sm:text-sm text-primary hover:underline mt-3 flex items-center gap-1"
          >
            <Edit3 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            Edit in Settings
          </button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Format the next newsletter date in a user-friendly way.
 * Shows "Today", "Tomorrow", or the full date for later dates.
 */
function formatNextNewsletterDate(dateStr: string | undefined): string {
  if (!dateStr) return '';

  try {
    // Parse the date string (YYYY-MM-DD format from the API)
    const [year, month, day] = dateStr.split('-').map(Number);
    const nextDate = new Date(year, month - 1, day);

    // Get today's date in the user's timezone
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Compare dates
    if (nextDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (nextDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      // Format as "Jan 15" or "Jan 15, 2027" if different year
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        ...(nextDate.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
      };
      return nextDate.toLocaleDateString('en-US', options);
    }
  } catch {
    return dateStr; // Return original if parsing fails
  }
}

/**
 * Build the next newsletter display value combining date and time.
 * Uses delivery schedules if available, falling back to preferences.
 */
function getNextNewsletterDisplay(
  preferences: Preferences | null,
  schedules: DeliverySchedule[] = []
): { value: string; subtext: string } {
  // Find the earliest active schedule
  const activeSchedules = schedules.filter(s => s.is_active);

  if (activeSchedules.length > 0) {
    // Sort by delivery time to find the earliest one
    const sortedSchedules = [...activeSchedules].sort((a, b) =>
      a.delivery_time.localeCompare(b.delivery_time)
    );
    const nextSchedule = sortedSchedules[0];
    const timezoneAbbr = nextSchedule.timezone_abbr || nextSchedule.timezone?.split('/')[1]?.replace('_', ' ') || '';

    // Multiple schedules display
    if (activeSchedules.length > 1) {
      return {
        value: nextSchedule.delivery_time,
        subtext: `${timezoneAbbr}${activeSchedules.length > 1 ? ` (+${activeSchedules.length - 1} more)` : ''}`
      };
    }

    return { value: nextSchedule.delivery_time, subtext: timezoneAbbr };
  }

  // Fall back to preferences
  if (!preferences) {
    return { value: 'Not set', subtext: '' };
  }

  const time = preferences.next_newsletter_time || preferences.delivery_time;
  const date = preferences.next_newsletter_date;
  const timezoneAbbr = preferences.timezone_abbr || preferences.timezone?.split('/')[1]?.replace('_', ' ') || '';

  if (!time) {
    return { value: 'Not set', subtext: '' };
  }

  // Format: "08:00" as main value, "Tomorrow, PST" as subtext
  const dateLabel = formatNextNewsletterDate(date);
  const subtext = dateLabel
    ? `${dateLabel}${timezoneAbbr ? `, ${timezoneAbbr}` : ''}`
    : timezoneAbbr;

  return { value: time, subtext };
}

/**
 * Format seconds into a human-readable string like "5m 30s" or "1h 15m"
 */
function formatElapsedTime(totalSeconds: number): string {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  } else if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
}

/**
 * Get the current stage info based on elapsed time.
 * Reports typically take 5-20 minutes:
 * - 0-5 min: Initializing
 * - 5-15 min: Researching
 * - 15-25 min: Writing
 * - 25-35 min: Fact-checking
 * - 35+ min: Finalizing
 */
function getGenerationStage(elapsedSeconds: number): { stage: string; icon: React.ReactNode; progress: number } {
  const minutes = elapsedSeconds / 60;

  if (minutes < 5) {
    return { stage: 'Initializing research agents...', icon: <Loader2 className="w-5 h-5 animate-spin" />, progress: 10 };
  } else if (minutes < 15) {
    return { stage: 'Researching topics & gathering sources...', icon: <Search className="w-5 h-5" />, progress: 30 };
  } else if (minutes < 25) {
    return { stage: 'Writing research content...', icon: <FileText className="w-5 h-5" />, progress: 55 };
  } else if (minutes < 35) {
    return { stage: 'Fact-checking & verifying sources...', icon: <CheckCircle2 className="w-5 h-5" />, progress: 75 };
  } else {
    return { stage: 'Finalizing report...', icon: <Sparkles className="w-5 h-5" />, progress: 90 };
  }
}

interface InProgressCardProps {
  topic: string;
  elapsedSeconds: number;
  onRefresh?: () => void;
  /** Recent issues for admin users */
  recentIssues?: RecentIssue[];
  /** Whether user is admin */
  isAdmin?: boolean;
  /** Real-time progress from SSE */
  realtimeProgress?: GenerationProgress | null;
  /** Polled progress from API (fallback when SSE not available) */
  polledProgress?: TaskProgress | null;
  /** Full active task data for displaying task ID and prompt */
  activeTask?: {
    task_id?: string;
    custom_prompt?: string | null;
    current_phase?: string | null;
    current_task_name?: string | null;
    total_tasks?: number;
    current_task_index?: number;
  };
}

// Collapsible prompt component (customer version)
function CollapsiblePrompt({ prompt, className = '' }: { prompt: string; className?: string }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const truncateLength = 150;
  const shouldTruncate = prompt.length > truncateLength;
  const displayText = shouldTruncate && !isExpanded
    ? prompt.slice(0, truncateLength) + '...'
    : prompt;

  return (
    <div className={`mt-3 ${className}`}>
      <div className="bg-surface-light/50 rounded-lg p-3 text-sm">
        <div className="flex items-center gap-1 text-text-muted mb-1">
          <FileText className="w-3 h-3" />
          <span className="text-xs font-medium">Custom Prompt</span>
        </div>
        <p className="text-text-secondary text-sm leading-relaxed">
          {displayText}
          {shouldTruncate && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="ml-2 text-primary hover:underline text-xs"
            >
              {isExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </p>
      </div>
    </div>
  );
}

function InProgressCard({ topic, elapsedSeconds, onRefresh, recentIssues = [], isAdmin = false, realtimeProgress, polledProgress, activeTask }: InProgressCardProps) {
  const { stage, icon, progress: estimatedProgress } = getGenerationStage(elapsedSeconds);
  const [showIssues, setShowIssues] = useState(false);
  const [showDetailedProgress, setShowDetailedProgress] = useState(true);

  // Filter to show only warnings and errors
  const significantIssues = recentIssues.filter(i => i.severity === 'warning' || i.severity === 'error' || i.severity === 'critical');
  const hasIssues = significantIssues.length > 0;

  // Use real-time progress if available, then polled progress, then activeTask fields, otherwise fall back to estimated
  const hasRealTimeProgress = realtimeProgress && realtimeProgress.sub_tasks.length > 0;
  const hasPolledProgress = polledProgress && polledProgress.total_steps > 0;
  const hasActiveTaskProgress = activeTask && (activeTask.total_tasks ?? 0) > 0;

  // Calculate progress from different sources
  const completedTasks = realtimeProgress?.sub_tasks.filter(t => t.status === 'completed').length || 0;
  const totalTasks = realtimeProgress?.total_tasks || polledProgress?.total_steps || activeTask?.total_tasks || 0;
  const currentStep = polledProgress?.step || (activeTask?.current_task_index ?? 0) + 1;

  // Calculate display progress
  let displayProgress: number;
  let currentPhase: string;
  let currentTaskDisplay: string | null = null;

  if (hasRealTimeProgress) {
    displayProgress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    currentPhase = realtimeProgress?.current_phase || stage;
  } else if (hasPolledProgress) {
    displayProgress = polledProgress.total_steps > 0 ? (currentStep / polledProgress.total_steps) * 100 : 0;
    currentPhase = polledProgress.stage_label || polledProgress.stage || stage;
  } else if (hasActiveTaskProgress) {
    displayProgress = totalTasks > 0 ? (currentStep / totalTasks) * 100 : 0;
    currentPhase = activeTask?.current_phase?.replace(/_/g, ' ') || stage;
    // Build task display like admin: "Task 3/10: Writing introduction"
    if (activeTask?.current_task_name) {
      currentTaskDisplay = `Task ${currentStep}/${totalTasks}: ${activeTask.current_task_name}`;
    }
  } else {
    displayProgress = estimatedProgress;
    currentPhase = stage;
  }

  return (
    <Card className={`bg-gradient-to-r from-primary/10 via-primary/5 to-transparent ${hasIssues ? 'border-warning/50' : 'border-primary/30'}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 animate-pulse ${hasIssues ? 'bg-warning/20' : 'bg-primary/20'}`}>
            {hasIssues ? <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-warning" /> : icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="warning" className="animate-pulse text-xs">Generating</Badge>
              <span className="text-xs sm:text-sm text-text-muted">{formatElapsedTime(elapsedSeconds)}</span>
              {(hasRealTimeProgress || hasPolledProgress || hasActiveTaskProgress) && (
                <span className="text-xs text-primary">
                  {hasRealTimeProgress ? `${completedTasks}/${totalTasks}` : `${currentStep}/${totalTasks}`} tasks
                </span>
              )}
              {isAdmin && hasIssues && (
                <Badge variant="error" className="text-xs">
                  {significantIssues.length} issue{significantIssues.length > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <h3 className="font-semibold mt-1 text-sm sm:text-base truncate">{topic}</h3>
            {/* Show current task name if available (like admin view) */}
            {currentTaskDisplay ? (
              <p className="text-sm text-primary mt-1">{currentTaskDisplay}</p>
            ) : (
              <p className="text-sm text-text-secondary mt-1">
                {(hasRealTimeProgress || hasPolledProgress || hasActiveTaskProgress)
                  ? currentPhase.replace(/_/g, ' ').replace(/task (\d+)/i, 'Task $1')
                  : stage}
              </p>
            )}

            {/* Progress bar */}
            <div className="mt-3 w-full bg-surface-light rounded-full h-2 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-1000 ease-out ${hasIssues ? 'bg-warning' : 'bg-primary'}`}
                style={{ width: `${displayProgress}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-text-muted">
                {(hasRealTimeProgress || hasPolledProgress || hasActiveTaskProgress)
                  ? `${Math.round(displayProgress)}% complete`
                  : 'Reports typically take 5-20 minutes'}
              </p>
              {hasRealTimeProgress && (
                <button
                  onClick={() => setShowDetailedProgress(!showDetailedProgress)}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  {showDetailedProgress ? 'Hide' : 'Show'} details
                  {showDetailedProgress ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
            </div>

            {/* Detailed real-time progress */}
            {hasRealTimeProgress && showDetailedProgress && (
              <div className="mt-4 border-t border-border pt-4">
                <TaskProgressPanel progress={realtimeProgress} compact />
              </div>
            )}

            {/* Admin: Show issues if any */}
            {isAdmin && hasIssues && (
              <div className="mt-3">
                <button
                  onClick={() => setShowIssues(!showIssues)}
                  className="text-xs text-warning hover:text-warning/80 flex items-center gap-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  {showIssues ? 'Hide' : 'Show'} generation issues
                  {showIssues ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                {showIssues && (
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {significantIssues.map((issue) => (
                      <div
                        key={issue.id}
                        className={`text-xs p-2 rounded ${
                          issue.severity === 'critical' ? 'bg-error/10 border-l-2 border-error' :
                          issue.severity === 'error' ? 'bg-error/10 border-l-2 border-error/70' :
                          'bg-warning/10 border-l-2 border-warning'
                        }`}
                      >
                        <div className="flex items-center gap-1 font-medium">
                          {issue.severity === 'critical' || issue.severity === 'error' ? (
                            <AlertCircle className="w-3 h-3 text-error" />
                          ) : (
                            <AlertTriangle className="w-3 h-3 text-warning" />
                          )}
                          <span className={issue.severity === 'critical' || issue.severity === 'error' ? 'text-error' : 'text-warning'}>
                            {issue.error_type}
                          </span>
                          <span className="text-text-muted ml-auto">
                            {new Date(issue.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        {issue.error_message && (
                          <p className="mt-1 text-text-secondary line-clamp-2">{issue.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Task ID display */}
            {activeTask?.task_id && (
              <div className="mt-3 flex items-center gap-2 text-xs text-text-muted">
                <span className="font-mono bg-surface-light px-2 py-1 rounded">
                  Task ID: {activeTask.task_id}
                </span>
              </div>
            )}

            {/* Custom prompt display (collapsible) */}
            {activeTask?.custom_prompt && (
              <CollapsiblePrompt prompt={activeTask.custom_prompt} />
            )}
          </div>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2 hover:bg-surface-light rounded-lg transition-colors"
            title="Refresh status"
          >
            <RefreshCw className="w-4 h-4 text-text-muted" />
          </button>
        )}
      </div>
    </Card>
  );
}

interface CompletedReportCardProps {
  topic?: string;
  historyId: string;
  onDismiss: () => void;
  onViewReport: () => void;
  s3Url?: string | null;
}

function CompletedReportCard({ topic, onDismiss, onViewReport, s3Url }: CompletedReportCardProps) {
  return (
    <Card className="bg-gradient-to-r from-success/10 via-success/5 to-transparent border-success/30">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-success/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="success" className="text-xs">Completed</Badge>
              <span className="text-xs sm:text-sm text-text-muted">Just now</span>
            </div>
            <h3 className="font-semibold mt-1 text-sm sm:text-base truncate">{topic || 'Newsletter'}</h3>
            <p className="text-xs sm:text-sm text-text-secondary mt-1">
              Your report is ready! View it now or find it in your History.
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-4">
              {s3Url ? (
                <Button
                  size="sm"
                  onClick={() => window.open(s3Url, '_blank')}
                  leftIcon={<ExternalLink className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  View Report
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={onViewReport}
                  leftIcon={<FileText className="w-4 h-4" />}
                  className="w-full sm:w-auto"
                >
                  View in History
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={onViewReport}
                rightIcon={<ArrowRight className="w-4 h-4" />}
                className="w-full sm:w-auto"
              >
                Go to History
              </Button>
            </div>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="p-2 hover:bg-surface-light rounded-lg transition-colors self-start"
          title="Dismiss"
        >
          <X className="w-4 h-4 text-text-muted" />
        </button>
      </div>
    </Card>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences, isLoading: prefsLoading, refresh: refreshPreferences } = usePreferences();
  const [canGenerate, setCanGenerate] = useState<CanGenerateResponse | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [completedReportS3Url, setCompletedReportS3Url] = useState<string | null>(null);
  const [deliverySchedules, setDeliverySchedules] = useState<DeliverySchedule[]>([]);

  // Track active newsletter generation tasks
  const {
    activeTasks,
    hasActiveTasks,
    elapsedSeconds,
    refresh: refreshActiveTasks,
    recentIssues,
    lastCompletedTask,
    clearCompletedTask,
  } = useActiveTask({
    pollInterval: 5000, // Poll every 5 seconds
    onComplete: (completedTask) => {
      // When generation completes, refresh data
      refreshPreferences();
      api.canGenerateNewsletter().then(setCanGenerate);

      // Fetch the completed newsletter to get the S3 URL
      if (completedTask?.history_id) {
        api.getNewsletterDetail(completedTask.history_id)
          .then((detail) => {
            setCompletedReportS3Url(detail.s3_url || null);
          })
          .catch((err) => {
            console.warn('Failed to fetch completed newsletter details:', err);
          });
      }
    }
  });

  // Get the active task's history_id for real-time progress streaming
  const activeHistoryId = activeTasks[0]?.history_id;

  // Real-time progress streaming via SSE
  const { progress: realtimeProgress } = useProgressStream(activeHistoryId || null, {
    enabled: hasActiveTasks && !!activeHistoryId,
    onComplete: () => {
      // SSE stream completed - refresh to get final state
      refreshActiveTasks();
    },
    onError: (error) => {
      console.warn('Progress stream error:', error);
      // Fall back to polling-based progress
    }
  });

  useEffect(() => {
    api.canGenerateNewsletter()
      .then(setCanGenerate)
      .catch((err) => {
        console.error('Failed to fetch can generate status:', err);
        // Default to allowing generation on error
        setCanGenerate({ can_generate: true, sent_today: 0, max_per_day: 999, remaining: 999 });
      });

    // Fetch delivery schedules for displaying next newsletter time
    api.getDeliverySchedules()
      .then((response) => setDeliverySchedules(response.schedules))
      .catch((err) => {
        console.warn('Failed to fetch delivery schedules:', err);
        // Will fall back to preferences.delivery_time
      });
  }, []);

  // Check if user has a topic configured
  const hasTopicConfigured = !!(preferences?.preset_id || preferences?.preset_name || preferences?.custom_prompt);

  const handleGenerateNow = async () => {
    // Check if topic is configured before generating
    if (!hasTopicConfigured) {
      setGenerateMessage({
        type: 'warning',
        text: 'NO_TOPIC_SET',
      });
      return;
    }

    setIsGenerating(true);
    setGenerateMessage(null);

    try {
      await api.generateNewsletter({
        topic: preferences?.preset_name || undefined,
        custom_prompt: preferences?.custom_prompt || undefined,
        send_email: true,
      });
      // Show a brief message, but the InProgressCard will take over
      setGenerateMessage({
        type: 'success',
        text: 'Newsletter generation started! You can track progress below.'
      });
      // Clear the message after 3 seconds since the card shows progress
      setTimeout(() => setGenerateMessage(null), 3000);

      // Optimistically update the count since task was queued successfully
      if (canGenerate) {
        setCanGenerate({
          ...canGenerate,
          sent_today: canGenerate.sent_today + 1,
          remaining: Math.max(0, canGenerate.remaining - 1),
          can_generate: canGenerate.remaining > 1,
          reason: canGenerate.remaining <= 1 ? `Daily limit reached (${canGenerate.max_per_day} newsletter(s) per day for your tier)` : undefined,
        });
      }
      // Refresh active tasks immediately to show the new task
      refreshActiveTasks();
      refreshPreferences();
    } catch (err) {
      setGenerateMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to generate newsletter'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const getStatusBadge = () => {
    const status = user?.subscription_status || 'none';
    if (status === 'trial') return <Badge variant="info">TRIAL ACTIVE</Badge>;
    if (status === 'active') return <Badge variant="success">ACTIVE</Badge>;
    if (status === 'expired') return <Badge variant="error">EXPIRED</Badge>;
    return <Badge variant="warning">{status.toUpperCase()}</Badge>;
  };

  // Compute next newsletter display with date
  // Uses delivery schedules if available, falls back to preferences
  const nextNewsletterDisplay = useMemo(() => {
    return getNextNewsletterDisplay(preferences, deliverySchedules);
  }, [preferences, deliverySchedules]);

  if (prefsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">
            Welcome back, {user?.first_name || 'there'}!
          </h1>
          <p className="text-text-secondary text-sm sm:text-base mt-1">
            Here's an overview of your research account
          </p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <StatCard
          icon={<Clock className="w-6 h-6 text-primary" />}
          label="Next Delivery"
          value={nextNewsletterDisplay.value}
          subtext={nextNewsletterDisplay.subtext}
        />
        <TopicCard
          topicName={preferences?.preset_name || (preferences?.custom_prompt ? 'Custom' : 'Not set')}
          promptTemplate={preferences?.prompt_template}
          customPrompt={preferences?.custom_prompt}
          presetId={preferences?.preset_id}
        />
        <StatCard
          icon={<FileText className="w-6 h-6 text-primary" />}
          label="Reports Generated Today"
          value={canGenerate ? `${canGenerate.sent_today}/${canGenerate.max_per_day}` : '-'}
          subtext={canGenerate ? `${canGenerate.remaining} remaining` : undefined}
        />
      </div>

      {/* In-Progress Report Cards - show all active tasks */}
      {hasActiveTasks && activeTasks.map((task, index) => {
        // Calculate elapsed seconds for each task individually
        const taskElapsedSeconds = task.started_at
          ? Math.floor((Date.now() - new Date(task.started_at).getTime()) / 1000)
          : 0;
        // SSE streaming only works for first task, others use polled progress
        const taskRealtimeProgress = index === 0 ? realtimeProgress : null;
        return (
          <InProgressCard
            key={task.task_id || task.history_id || index}
            topic={task.topic || 'Newsletter'}
            elapsedSeconds={index === 0 ? elapsedSeconds : Math.max(0, taskElapsedSeconds)}
            onRefresh={refreshActiveTasks}
            recentIssues={index === 0 ? recentIssues : []}
            isAdmin={user?.is_master_user}
            realtimeProgress={taskRealtimeProgress}
            polledProgress={task.progress}
            activeTask={task}
          />
        );
      })}

      {/* Completed Report Card - shown when a task just completed */}
      {lastCompletedTask && !hasActiveTasks && (
        <CompletedReportCard
          topic={lastCompletedTask.topic}
          historyId={lastCompletedTask.history_id}
          s3Url={completedReportS3Url}
          onDismiss={() => {
            clearCompletedTask();
            setCompletedReportS3Url(null);
          }}
          onViewReport={() => navigate('/history')}
        />
      )}

      {/* Trial Banner */}
      {user?.trial_days_remaining !== undefined && user.trial_days_remaining > 0 && (
        <Card className="bg-gradient-to-r from-primary/20 to-primary/5 border-primary/30">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
              <div>
                <p className="font-semibold text-sm sm:text-base">
                  {user.trial_days_remaining} days remaining in your trial
                </p>
                <p className="text-text-secondary text-xs sm:text-sm">
                  Upgrade to continue receiving research reports
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Crown className="w-4 h-4" />}
              onClick={() => navigate('/dashboard/plans')}
              className="w-full sm:w-auto"
            >
              Upgrade Now
            </Button>
          </div>
        </Card>
      )}

      {/* Founder Account Banner */}
      {user?.is_founder && (
        <Card className="bg-gradient-to-r from-primary/20 to-primary/5 border-primary/30">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="relative">
                <Crown className="w-6 h-6 sm:w-8 sm:h-8 text-primary flex-shrink-0" />
                <Gift className="w-3 h-3 sm:w-4 sm:h-4 text-primary absolute -bottom-1 -right-1" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-primary text-background">
                    FOUNDER
                  </span>
                  <p className="font-semibold text-sm sm:text-base">
                    Welcome, Founder!
                  </p>
                </div>
                <p className="text-text-secondary text-xs sm:text-sm">
                  You have full access to ALL premium AI models. Your credits never expire - top up or subscribe when you need more.
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Sparkles className="w-4 h-4" />}
              onClick={() => navigate('/dashboard/plans')}
              className="w-full sm:w-auto"
            >
              Get More Credits
            </Button>
          </div>
        </Card>
      )}

      {/* Generate Message */}
      {generateMessage && (
        <Alert
          variant={generateMessage.type}
          onClose={() => setGenerateMessage(null)}
        >
          {generateMessage.text === 'NO_TOPIC_SET' ? (
            <>
              No topic has been set up yet. Please{' '}
              <Link to="/preferences" className="text-primary hover:underline font-medium">
                configure your newsletter settings
              </Link>{' '}
              before generating a report.
            </>
          ) : (
            generateMessage.text
          )}
        </Alert>
      )}

      {/* Quick Actions */}
      <Card>
        <h3 className="font-semibold mb-4">Quick Actions</h3>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
          <Button
            onClick={handleGenerateNow}
            isLoading={isGenerating}
            disabled={canGenerate === null || !canGenerate.can_generate || hasActiveTasks}
            leftIcon={hasActiveTasks ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            className="w-full sm:w-auto"
          >
            {hasActiveTasks ? 'Generating...' : 'Generate Now'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/preferences')}
            rightIcon={<ArrowRight className="w-4 h-4" />}
            className="w-full sm:w-auto"
          >
            Change Settings
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/history')}
            rightIcon={<ArrowRight className="w-4 h-4" />}
            className="w-full sm:w-auto"
          >
            View History
          </Button>
        </div>
        {/* Generation time notice */}
        <p className="text-text-muted text-xs sm:text-sm mt-3 flex items-start sm:items-center gap-1.5">
          <Clock className="w-4 h-4 flex-shrink-0 mt-0.5 sm:mt-0" />
          <span>Reports typically take 5-20 minutes to generate due to thorough research and fact-checking.</span>
        </p>
        {/* Daily limit reached message */}
        {canGenerate && !canGenerate.can_generate && canGenerate.remaining === 0 && (
          <div className="mt-4 p-4 bg-warning/10 border border-warning/20 rounded-lg">
            <p className="text-warning font-medium mb-2">Daily limit reached</p>
            <p className="text-text-secondary text-sm">
              You've used all {canGenerate.max_per_day} report{canGenerate.max_per_day > 1 ? 's' : ''} for today.{' '}
              <Link to="/dashboard/plans" className="text-primary hover:underline font-medium">
                Upgrade your plan
              </Link>{' '}
              to get more daily reports, or{' '}
              <Link to="/history" className="text-primary hover:underline font-medium">
                view your generated reports
              </Link>.
            </p>
          </div>
        )}
        {/* No topic configured message */}
        {canGenerate && !canGenerate.can_generate && canGenerate.reason?.toLowerCase().includes('no topic') && (
          <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-lg">
            <p className="text-primary font-medium mb-2">No topic configured</p>
            <p className="text-text-secondary text-sm">
              Please{' '}
              <Link to="/preferences" className="text-primary hover:underline font-medium">
                set up your newsletter settings
              </Link>{' '}
              to choose a topic before generating.
            </p>
          </div>
        )}
        {/* Other can't generate reasons (subscription issues, etc.) */}
        {canGenerate && !canGenerate.can_generate && canGenerate.remaining !== 0 && !canGenerate.reason?.toLowerCase().includes('no topic') && (
          <p className="text-text-muted text-sm mt-3">{canGenerate.reason}</p>
        )}
      </Card>
    </div>
  );
}

